// jQuery-like dynamic type coercer
export class TypeCoercer {
    // Coerce a single value
    static coerce(value: unknown): unknown {
        if (value == null) return value; // null/undefined passthrough
        if (typeof value !== "string") {
            if (Array.isArray(value)) return value.map(v => TypeCoercer.coerce(v));
            if (typeof value === "object") return value; // leave non-string objects as-is
            return value; // number/boolean/symbol/bigint/function: passthrough
        }

        const s = value.trim();
        if (s === "") return "";               // keep empty string as empty string
        if (s === "true") return true;
        if (s === "false") return false;
        if (s === "null") return null;
        if (s === "undefined") return undefined;

        // Number (finite)
        // Accepts: 123, -1.23, .5, 1e3, -1E-2
        if (/^[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/.test(s)) {
            const n = Number(s);
            if (Number.isFinite(n)) return n;
        }

        // JSON object/array
        const first = s[0];
        if ((first === "{" || first === "[")) {
            try {
                return JSON.parse(s);
            } catch {
                // fall through and return the original string if invalid JSON
            }
        }

        // Default: original string
        return s;
    }

    // Shallow-coerce all own enumerable values of an object
    static coerceObject<T extends Record<string, unknown>>(obj: T): T {
        const out: Record<string, unknown> = {};
        for (const k of Object.keys(obj)) {
            out[k] = TypeCoercer.coerce(obj[k]);
        }
        return out as T;
    }

    // Deep-coerce: walks arrays/objects recursively, coercing strings at the leaves
    static coerceDeep<T>(val: T): T {
        if (val == null) return val;
        if (typeof val === "string") return TypeCoercer.coerce(val) as unknown as T;
        if (Array.isArray(val)) return val.map(v => TypeCoercer.coerceDeep(v)) as unknown as T;
        if (typeof val === "object") {
            const src = val as Record<string, unknown>;
            const out: Record<string, unknown> = {};
            for (const k of Object.keys(src)) {
                out[k] = TypeCoercer.coerceDeep(src[k]);
            }
            return out as T;
        }
        return val;
    }
}
